import { useState, useCallback, useEffect } from 'react';

export interface ColumnWidths {
    [columnKey: string]: number;
}

const getStorageKey = (tableKey: string) => `column_widths_${tableKey}`;

const loadWidths = (tableKey: string): ColumnWidths => {
    try {
        const saved = localStorage.getItem(getStorageKey(tableKey));
        if (saved) return JSON.parse(saved);
    } catch {
        // ignore
    }
    return {};
};

const saveWidths = (tableKey: string, widths: ColumnWidths) => {
    try {
        localStorage.setItem(getStorageKey(tableKey), JSON.stringify(widths));
    } catch {
        // ignore
    }
};

// Module-level subscriber registry so multiple hook instances with the same
// tableKey stay in sync (e.g. child table + parent settings button).
const subscribers = new Map<string, Set<(widths: ColumnWidths) => void>>();

const notify = (tableKey: string, widths: ColumnWidths) => {
    subscribers.get(tableKey)?.forEach(cb => cb(widths));
};

export const useColumnWidths = (tableKey: string) => {
    const [widths, setWidths] = useState<ColumnWidths>(() => loadWidths(tableKey));

    // Subscribe to cross-instance updates for the same tableKey
    useEffect(() => {
        if (!subscribers.has(tableKey)) {
            subscribers.set(tableKey, new Set());
        }
        const subs = subscribers.get(tableKey)!;
        const handler = (newWidths: ColumnWidths) => setWidths(newWidths);
        subs.add(handler);
        return () => { subs.delete(handler); };
    }, [tableKey]);

    const updateWidth = useCallback((columnKey: string, width: number) => {
        setWidths(prev => {
            const updated = { ...prev, [columnKey]: width };
            saveWidths(tableKey, updated);
            // Notify other hook instances with the same key
            notify(tableKey, updated);
            return updated;
        });
    }, [tableKey]);

    const resetWidths = useCallback(() => {
        const empty: ColumnWidths = {};
        setWidths(empty);
        try {
            localStorage.removeItem(getStorageKey(tableKey));
        } catch {
            // ignore
        }
        notify(tableKey, empty);
    }, [tableKey]);

    const hasCustomWidths = Object.keys(widths).length > 0;

    return { widths, updateWidth, resetWidths, hasCustomWidths };
};
